import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { MonitorCheck, ClipboardCheck, Gauge, CalendarClock, Bell } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth, MODULES } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import ComplianceTab from '@/pages/monitoring/tabs/ComplianceTab';
import MetricsHealthTab from '@/pages/monitoring/tabs/MetricsHealthTab';
import ConsolidatedCoverageTab from '@/pages/monitoring/tabs/ConsolidatedCoverageTab';
import NotificationsTab from '@/pages/monitoring/tabs/NotificationsTab';

/**
 * Centro de Monitoreo de los centros de lavado.
 *
 * Reemplaza al antiguo "Monitor de Registros Diarios" (misma ruta,
 * /monitor-registros) ampliándolo a cuatro frentes: completitud de digitación,
 * métricas contra los parámetros de cada centro, cobertura de consolidados
 * mensuales y la bandeja de notificaciones.
 *
 * Las pestañas de métricas y consolidados exigen el módulo `reports`; con solo
 * `workplaces` se ve únicamente la completitud y las notificaciones.
 */
const MonitoringCenterPage = () => {
  const { hasPermission } = useAuth();
  const { noLeidas, refresh } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();

  const canSeeMetrics = hasPermission(MODULES.REPORTS);

  const TABS = [
    { id: 'registros', label: 'Registros diarios', icon: ClipboardCheck, enabled: true },
    { id: 'metricas', label: 'Métricas vs parámetros', icon: Gauge, enabled: canSeeMetrics },
    { id: 'consolidados', label: 'Consolidados', icon: CalendarClock, enabled: canSeeMetrics },
    { id: 'notificaciones', label: 'Notificaciones', icon: Bell, enabled: true },
  ].filter((t) => t.enabled);

  const requested = searchParams.get('tab');
  const initial = TABS.some((t) => t.id === requested) ? requested : TABS[0].id;
  const [tab, setTab] = useState(initial);

  // La campana enlaza a ?tab=notificaciones; mantenemos la URL sincronizada
  // para que la pestaña activa sea compartible y sobreviva a un refresh.
  useEffect(() => {
    if (requested !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-4 md:p-6 space-y-5"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-indigo-500/15 p-3 rounded-lg">
              <MonitorCheck className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">Centro de Monitoreo</CardTitle>
              <CardDescription className="mt-1">
                Control transversal de los centros de lavado: qué se digitó, qué métricas se
                salieron de parámetro y qué consolidados faltan.
              </CardDescription>
            </div>
          </div>
          {noLeidas > 0 && (
            <button
              type="button"
              onClick={() => setTab('notificaciones')}
              className="shrink-0"
              title="Ver notificaciones sin leer"
            >
              <Badge variant="destructive" className="gap-1.5 px-3 py-1">
                <Bell className="h-3.5 w-3.5" />
                {noLeidas} sin leer
              </Badge>
            </button>
          )}
        </CardHeader>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} className="gap-2">
                <Icon className="h-4 w-4" />
                {t.label}
                {t.id === 'notificaciones' && noLeidas > 0 && (
                  <span className="ml-1 rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground tabular-nums">
                    {noLeidas > 99 ? '99+' : noLeidas}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="registros" className="mt-4">
          <ComplianceTab onNotificationsChanged={refresh} />
        </TabsContent>

        {canSeeMetrics && (
          <TabsContent value="metricas" className="mt-4">
            <MetricsHealthTab onNotificationsChanged={refresh} />
          </TabsContent>
        )}

        {canSeeMetrics && (
          <TabsContent value="consolidados" className="mt-4">
            <ConsolidatedCoverageTab />
          </TabsContent>
        )}

        <TabsContent value="notificaciones" className="mt-4">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default MonitoringCenterPage;
